"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { formatPhoneForDisplay } from "@/lib/phone/normalize";
import { telHref } from "@/lib/phone/normalize";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Shield,
  MapPin,
  Calendar,
  Mail,
  Phone,
  Navigation,
  Info,
  Copy,
  Check,
  Instagram,
  Facebook,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ReviewCard from "@/components/review/ReviewCard";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ImageModal } from "@/components/ImageModal";
import RealProductCard from "@/components/RealProductCard";
import type {
  PartnerProfile,
  PartnerBranch,
  PartnerReview,
  Product,
} from "@/types";
import { loadPartnerProductsPageAction } from "@/app/actions/products";
import PartnerProfileCard from "./PartnerProfileCard";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/utils/clipboard";
import {
  getBranchAddressDisplay,
  getBranchRowAddressDisplay,
} from "@/lib/branches/local-address";
import BranchAddressLines from "@/components/shared/BranchAddressLines";
import {
  filterBranchesByQuery,
  groupBranchesByRegion,
} from "@/lib/utils/branch-groups";

const PRODUCTS_PER_PAGE = 10;

/**
 * 이 수를 넘으면 지점 하나당 탭 하나를 만들지 않는다.
 * 파트너 한 곳이 247개 지점을 갖는 케이스가 실제로 있어서, 알약 탭이 화면을
 * 수십 줄 밀어낸다. 대신 시/도 그룹 탭 + 지점 목록 + 검색 모달로 바꾼다.
 */
const GROUPED_BRANCH_THRESHOLD = 12;

/** 지점/그룹 알약 탭 공통 클래스 — 기존 지점 탭과 동일한 생김새. */
const BRANCH_TAB_CLASS =
  "px-4 py-2 rounded-full text-sm font-normal data-[state=active]:bg-orange-500 data-[state=active]:text-white border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors";

// Branch Image Carousel using shadcn/ui Carousel
function BranchImageCarousel({
  images,
  branchName,
}: {
  images: {
    id: string;
    image_url: string;
    alt_text?: string;
    display_order: number;
  }[];
  branchName: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  // Convert images for ImageModal
  const modalImages = images.map((img, idx) => ({
    url: img.image_url,
    name: img.alt_text || `${branchName} ${idx + 1}`,
  }));

  const openModal = (index: number) => {
    setModalIndex(index);
    setModalOpen(true);
  };

  if (images.length === 0) return null;

  if (images.length === 1) {
    return (
      <>
        <div
          className="aspect-4/3 rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
          onClick={() => openModal(0)}
        >
          <OptimizedImage
            src={images[0].image_url}
            alt={branchName}
            width={400}
            height={300}
            className="w-full h-full object-contain"
          />
        </div>
        <ImageModal
          images={modalImages}
          currentIndex={modalIndex}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onIndexChange={setModalIndex}
        />
      </>
    );
  }

  return (
    <>
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full"
      >
        <div className="flex items-center gap-2">
          <CarouselPrevious className="relative left-0 translate-x-0 translate-y-0 h-8 w-8 bg-gray-100 hover:bg-gray-200 border-0" />
          <div className="flex-1 aspect-4/3 rounded-xl overflow-hidden">
            <CarouselContent className="ml-0 h-full">
              {images.map((img, idx) => (
                <CarouselItem key={img.id} className="pl-0 h-full">
                  <div
                    className="h-full bg-gray-100 cursor-pointer"
                    onClick={() => openModal(idx)}
                  >
                    <OptimizedImage
                      src={img.image_url}
                      alt={img.alt_text || `${branchName} ${idx + 1}`}
                      width={400}
                      height={300}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>
          <CarouselNext className="relative right-0 translate-x-0 translate-y-0 h-8 w-8 bg-gray-100 hover:bg-gray-200 border-0" />
        </div>
      </Carousel>
      <ImageModal
        images={modalImages}
        currentIndex={modalIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onIndexChange={setModalIndex}
      />
    </>
  );
}

interface PartnerPageClientProps {
  initialPartner: PartnerProfile;
  initialProducts: Product[];
  initialTotalProducts: number;
  initialBranches: PartnerBranch[];
  initialReviews: PartnerReview[];
  locale: string;
}


/**
 * 지점 목록 행의 주소 한 줄 — 상세 주소 없이 번역 주소만, 라벨 없이(원문은 상세 패널의
 * 둘째 줄에서). 번역 주소가 비어 있는 일본 지점만 원문 주소로 폴백(local-address).
 */
function BranchRowAddress({ branch }: { branch: PartnerBranch }) {
  const locale = useLocale();
  const { primary } = getBranchAddressDisplay({
    locale,
    country: branch.country,
    localAddress: branch.address_local,
    translatedAddress: branch.address,
  });
  return (
    <span className="mt-0.5 block truncate text-xs text-gray-500">
      {primary}
    </span>
  );
}

/**
 * 지점 1곳의 상세 패널(사진/주소/연락처/영업시간).
 * 지점 수가 적을 때의 탭 콘텐츠와, 많을 때의 그룹 목록 선택 결과가 같은 마크업을
 * 쓰도록 분리했다.
 */
function BranchDetailPanel({
  branch,
  t,
  copiedBranchId,
  onCopyAddress,
}: {
  branch: PartnerBranch;
  t: ReturnType<typeof useTranslations>;
  copiedBranchId: string | null;
  onCopyAddress: (branch: PartnerBranch) => void;
}) {
  const locale = useLocale();
  // 일본 지점(일본어 외 화면)은 번역 주소 + 둘째 줄 현지 원문 주소(local-address).
  const address = getBranchRowAddressDisplay(branch, locale);
  // Prepare images array
  const branchImages =
    branch.images && branch.images.length > 0
      ? branch.images.sort((a, b) => a.display_order - b.display_order)
      : branch.image_url
      ? [
          {
            id: "main",
            image_url: branch.image_url,
            display_order: 0,
          },
        ]
      : [];

  return (
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Branch Image Carousel */}
            {branchImages.length > 0 && (
              <BranchImageCarousel
                images={branchImages}
                branchName={branch.name}
              />
            )}

            {/* Branch Info */}
            <div
              className={`space-y-4 ${
                branchImages.length > 0
                  ? "md:col-span-2"
                  : "md:col-span-3"
              }`}
            >
              {/* Badges */}
              <div className="flex gap-1.5">
                {(branch.location_type === "both" ||
                  branch.location_type === "pickup") && (
                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">
                    {t("partner.branches.locationTypes.pickup")}
                  </Badge>
                )}
                {(branch.location_type === "both" ||
                  branch.location_type === "return") && (
                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">
                    {t("partner.branches.locationTypes.return")}
                  </Badge>
                )}
              </div>

              {/* Address & Phone */}
              <div className="space-y-2">
                <div className="text-sm text-gray-600 flex items-start gap-2">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                  {address.isLocal ? (
                    <BranchAddressLines
                      display={address}
                      className="flex-1 wrap-break-word"
                    />
                  ) : (
                    <span className="flex-1 min-w-0 wrap-break-word">
                      {branch.address}
                      {branch.address_detail &&
                        ` ${branch.address_detail}`}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onCopyAddress(branch)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all shrink-0 ${
                      copiedBranchId === branch.id
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {copiedBranchId === branch.id ? (
                      <>
                        <Check className="h-3 w-3" />
                        {t("partner.branches.copied")}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        {address.isLocal
                          ? t("product.locationMap.copyLocal")
                          : t("partner.branches.copyAddress")}
                      </>
                    )}
                  </button>
                </div>
                {branch.phone && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Phone className="w-4 h-4 shrink-0 text-gray-400" />
                    <a
                      href={telHref(branch.phone)}
                      className="hover:text-orange-600 transition-colors"
                    >
                      {formatPhoneForDisplay(branch.phone)}
                    </a>
                  </p>
                )}
                {branch.directions && (
                  <p className="text-sm text-gray-600 flex items-start gap-2">
                    <Navigation className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                    <span>
                      <span className="font-medium text-gray-700">
                        {t("partner.branches.directions")}
                      </span>{" "}
                      {branch.directions}
                    </span>
                  </p>
                )}
                {branch.notes && (
                  <p className="text-sm text-gray-600 flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                    <span>
                      <span className="font-medium text-gray-700">
                        {t("partner.branches.notes")}
                      </span>{" "}
                      {branch.notes}
                    </span>
                  </p>
                )}
              </div>

              {/* Operating Hours Grid */}
              {branch.operating_hours &&
                branch.operating_hours.length > 0 && (
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {t("partner.branches.hours")}
                      </span>
                    </div>
                    <div className="grid grid-cols-7 gap-2 p-3 bg-white rounded-lg">
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                        const hours = branch.operating_hours?.find(
                          (h) => h.day_of_week === dayIndex,
                        );
                        const isWeekend =
                          dayIndex === 0 || dayIndex === 6;
                        const is24Hours =
                          hours?.open_time?.slice(0, 5) ===
                            "00:00" &&
                          hours?.close_time?.slice(0, 5) ===
                            "23:59";

                        return (
                          <div
                            key={dayIndex}
                            className="text-center"
                          >
                            <div
                              className={`text-xs font-medium mb-1 ${
                                isWeekend
                                  ? "text-orange-600"
                                  : "text-gray-600"
                              }`}
                            >
                              {t(`partner.dayAbbr.${dayIndex}`)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {hours?.is_closed ? (
                                <span className="text-gray-300">
                                  -
                                </span>
                              ) : is24Hours ? (
                                <span className="text-orange-500 font-medium">
                                  24h
                                </span>
                              ) : (
                                <>
                                  <div>
                                    {hours?.open_time?.slice(0, 5)}
                                  </div>
                                  <div>
                                    {hours?.close_time?.slice(0, 5)}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
  );
}

/**
 * 지점이 많은 파트너(최대 247곳)용 지점 탐색기.
 *
 * 시/도 그룹 탭("전체" + 그룹별 지점 수) → 그룹 안 지점 목록(평평한 divide 리스트)
 * → 선택한 지점 상세. 그룹 수 자체도 많을 수 있어서 "지점 전체 보기" 모달에
 * 전체 검색을 따로 둔다.
 */
function GroupedBranchBrowser({
  branches,
  t,
  copiedBranchId,
  onCopyAddress,
}: {
  branches: PartnerBranch[];
  t: ReturnType<typeof useTranslations>;
  copiedBranchId: string | null;
  onCopyAddress: (branch: PartnerBranch) => void;
}) {
  const groups = useMemo(
    () => groupBranchesByRegion(branches, t("partner.branches.otherRegion")),
    [branches, t],
  );

  const [activeGroup, setActiveGroup] = useState("all");
  const [selectedBranchId, setSelectedBranchId] = useState(
    branches[0]?.id ?? "",
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const visibleBranches =
    activeGroup === "all"
      ? branches
      : (groups.find((group) => group.key === activeGroup)?.branches ?? branches);

  const selectedBranch =
    visibleBranches.find((branch) => branch.id === selectedBranchId) ??
    visibleBranches[0];

  const searchResults = useMemo(
    () => filterBranchesByQuery(branches, query),
    [branches, query],
  );

  const handleGroupChange = (next: string) => {
    setActiveGroup(next);
    const nextBranches =
      next === "all"
        ? branches
        : (groups.find((group) => group.key === next)?.branches ?? branches);
    if (!nextBranches.some((branch) => branch.id === selectedBranchId)) {
      setSelectedBranchId(nextBranches[0]?.id ?? "");
    }
  };

  const handlePickFromSearch = (branchId: string) => {
    setSelectedBranchId(branchId);
    // 현재 그룹에 없는 지점을 골랐으면 그 지점이 속한 그룹으로 이동한다.
    if (
      activeGroup !== "all" &&
      !visibleBranches.some((branch) => branch.id === branchId)
    ) {
      const owner = groups.find((group) =>
        group.branches.some((branch) => branch.id === branchId),
      );
      setActiveGroup(owner?.key ?? "all");
    }
    setSearchOpen(false);
    setQuery("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-2">
        <Tabs
          value={activeGroup}
          onValueChange={handleGroupChange}
          className="min-w-0 flex-1"
        >
          <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-transparent p-0">
            <TabsTrigger value="all" className={BRANCH_TAB_CLASS}>
              {t("partner.branches.allRegions")}
              <span className="ml-1.5 tabular-nums opacity-70">
                {branches.length}
              </span>
            </TabsTrigger>
            {groups.map((group) => (
              <TabsTrigger
                key={group.key}
                value={group.key}
                className={BRANCH_TAB_CLASS}
              >
                {group.label}
                <span className="ml-1.5 tabular-nums opacity-70">
                  {group.branches.length}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="shrink-0 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
        >
          {t("partner.branches.viewAllBranches")}
        </button>
      </div>

      <div className="max-h-72 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 divide-y divide-gray-100">
        {visibleBranches.map((branch) => (
          <button
            key={branch.id}
            type="button"
            aria-current={branch.id === selectedBranch?.id}
            onClick={() => setSelectedBranchId(branch.id)}
            className={cn(
              "block w-full px-4 py-3 text-left",
              branch.id === selectedBranch?.id
                ? "bg-gray-50"
                : "hover:bg-gray-50",
            )}
          >
            <span
              className={cn(
                "block truncate text-sm text-gray-900",
                branch.id === selectedBranch?.id && "font-medium",
              )}
            >
              {branch.name}
            </span>
            <BranchRowAddress branch={branch} />
          </button>
        ))}
      </div>

      {selectedBranch && (
        <BranchDetailPanel
          branch={selectedBranch}
          t={t}
          copiedBranchId={copiedBranchId}
          onCopyAddress={onCopyAddress}
        />
      )}

      <Dialog
        open={searchOpen}
        onOpenChange={(next) => {
          setSearchOpen(next);
          if (!next) setQuery("");
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-base font-medium">
              {t("partner.branches.viewAllBranches")}
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 py-3">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("partner.branches.searchPlaceholder")}
              aria-label={t("partner.branches.searchPlaceholder")}
              className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none placeholder:text-gray-400 focus:border-gray-400"
            />
          </div>
          <div className="max-h-[60vh] overflow-y-auto overscroll-contain border-t border-gray-200 divide-y divide-gray-100">
            {searchResults.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                {t("partner.branches.noResults")}
              </p>
            ) : (
              searchResults.map((branch) => (
                <button
                  key={branch.id}
                  type="button"
                  onClick={() => handlePickFromSearch(branch.id)}
                  className="block w-full px-4 py-3 text-left hover:bg-gray-50"
                >
                  <span className="block truncate text-sm text-gray-900">
                    {branch.name}
                  </span>
                  <BranchRowAddress branch={branch} />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PartnerPageClient({
  initialPartner,
  initialProducts,
  initialTotalProducts,
  initialBranches,
  initialReviews,
  locale,
}: PartnerPageClientProps) {
  const t = useTranslations();
  const partner = initialPartner;
  const branches = initialBranches;
  const [reviews, setReviews] = useState<PartnerReview[]>(initialReviews);

  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [totalProducts] = useState(initialTotalProducts);
  const [currentOffset, setCurrentOffset] = useState(PRODUCTS_PER_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);

  // Copy branch address to clipboard (full address + detail), with a brief
  // per-branch "copied" confirmation. Mirrors LocationMap / BusinessInfoModal.
  const [copiedBranchId, setCopiedBranchId] = useState<string | null>(null);
  // 일본 지점(일본어 외 화면)은 현지 원문 주소를 복사한다(local-address).
  const handleCopyAddress = async (branch: PartnerBranch) => {
    const { copyText } = getBranchRowAddressDisplay(branch, locale);
    const ok = await copyToClipboard(copyText);
    if (!ok) return;
    setCopiedBranchId(branch.id);
    toast.success(t("partner.branches.addressCopied"));
    setTimeout(() => setCopiedBranchId(null), 2000);
  };

  // Reviews carousel: desktop left/right nav buttons. One card-width per
  // click matches the natural visual rhythm of the 320px fixed cards.
  const reviewSliderRef = useRef<HTMLDivElement>(null);
  const scrollReviewSlider = (direction: "left" | "right") => {
    if (!reviewSliderRef.current) return;
    const cardStep = 320 + 12; // card width + gap
    reviewSliderRef.current.scrollBy({
      left: direction === "left" ? -cardStep : cardStep,
      behavior: "smooth",
    });
  };

  // On-demand translation for reviews whose SSR-resolved `translated_comment`
  // is null (no row in review_translations for this locale yet). Mirrors the
  // pattern in ProductReviews.tsx — original comment stays visible during
  // fetch so there's no UX regression. Cleared whenever locale changes.
  const translationAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    translationAttemptedRef.current = new Set();
  }, [locale]);

  useEffect(() => {
    const toTranslate = reviews.filter(
      (r) =>
        !r.translated_comment &&
        !translationAttemptedRef.current.has(r.id) &&
        !!r.comment &&
        !!r.source_lang &&
        r.source_lang !== locale,
    );
    if (toTranslate.length === 0) return;

    toTranslate.forEach((r) => translationAttemptedRef.current.add(r.id));

    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        toTranslate.map(async (r) => {
          const response = await fetch("/api/translate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              entity_type: "product_review",
              entity_id: r.id,
              source_lang: r.source_lang,
              target_lang: locale,
            }),
          });
          if (!response.ok) return null;
          const result = await response.json();
          const translation = result?.data?.comment as string | undefined;
          return translation ? { id: r.id, translation } : null;
        }),
      );
      if (cancelled) return;

      const successful = results
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        .filter((x): x is { id: string; translation: string } => !!x);
      if (successful.length === 0) return;

      setReviews((prev) =>
        prev.map((r) => {
          const hit = successful.find((s) => s.id === r.id);
          return hit ? { ...r, translated_comment: hit.translation } : r;
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [reviews, locale]);

  // Load more products (server action — 마크업 요율은 서버에서만 읽는다)
  const loadMoreProducts = async () => {
    if (loadingMore) return;

    try {
      setLoadingMore(true);
      const result = await loadPartnerProductsPageAction(partner.id, locale, {
        limit: PRODUCTS_PER_PAGE,
        offset: currentOffset,
      });
      // Filter out duplicates by checking existing product IDs
      setProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newProducts = result.products.filter(
          (p) => !existingIds.has(p.id),
        );
        return [...prev, ...newProducts];
      });
      setCurrentOffset((prev) => prev + PRODUCTS_PER_PAGE);
    } catch (error) {
      console.error("Failed to load more products:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMoreProducts = products.length < totalProducts;

  return (
    <div className="min-h-screen bg-white">
      {/* Cover Image - 1200x400 (3:1 ratio) */}
      {partner.cover_image_url && (
        <div className="w-full bg-gray-white">
          <div className="relative w-full max-h-80 m-auto aspect-3/1 border-b border-gray-100">
            <OptimizedImage
              src={partner.cover_image_url}
              alt={t("partner.coverAlt", { name: partner.name })}
              fill
              sizes="(max-width: 1000px) 100vw, 1000px"
              priority
              className="object-contain"
            />
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-8 sm:pb-12 pt-6 sm:pt-10">
        {/* Profile Header - Simple & Clean */}
        {/* 커버가 있으면 카드가 커버 하단에 살짝 걸치도록 끌어올린다.
            (커버 이미지는 fill=absolute 라 positioned — 카드도 relative z-10 이어야 위로 올라온다) */}
        <div
          className={cn(
            "pb-8 border-b border-gray-100",
            partner.cover_image_url && "-mt-16 sm:-mt-24",
          )}
        >
          <div className="flex flex-col items-center text-center">
            <PartnerProfileCard
              partner={partner}
              branchCount={branches.length}
            />

            {/* Description - Full text with line breaks */}
            {partner.description && (
              <p className="text-gray-500 mt-4 text-sm leading-relaxed max-w-lg whitespace-pre-line">
                {partner.description}
              </p>
            )}

            {/* Contact & Social - Icon Only */}
            <div className="flex items-center gap-2 mt-5">
              {partner.phone && (
                <a
                  href={telHref(partner.phone)}
                  className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  title={formatPhoneForDisplay(partner.phone)}
                >
                  <Phone className="w-5 h-5" />
                </a>
              )}
              {partner.email && (
                <a
                  href={`mailto:${partner.email}`}
                  className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  title={partner.email}
                >
                  <Mail className="w-5 h-5" />
                </a>
              )}
              {partner.instagram_url && (
                <a
                  href={partner.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-pink-600 hover:bg-pink-50 rounded-full transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {partner.facebook_url && (
                <a
                  href={partner.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {partner.kakao_channel_url && (
                <a
                  href={partner.kakao_channel_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Products Section */}
        {products && products.length > 0 && (
          <div className="py-12 border-b border-gray-200">
            <div className="mb-8">
              <h2 className="text-2xl font-normal text-gray-900 mb-2">
                {t("partner.products.title", { name: partner.name })}
              </h2>
              <p className="text-sm text-gray-500">
                {t("partner.products.description", { count: totalProducts })}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {products.map((product) => (
                <RealProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Load More Button */}
            {hasMoreProducts && (
              <div className="flex justify-center mt-10">
                <Button
                  onClick={loadMoreProducts}
                  disabled={loadingMore}
                  className="px-8 py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {loadingMore
                    ? t("partner.products.loading")
                    : t("partner.products.loadMore")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Locations Section with Tabs */}
        {branches && branches.length > 0 && (
          <div className="py-12 border-b border-gray-200">
            <h2 className="text-xl font-medium text-gray-900 mb-6">
              {t("partner.branches.title")}
            </h2>

            {branches.length > GROUPED_BRANCH_THRESHOLD ? (
              <GroupedBranchBrowser
                branches={branches}
                t={t}
                copiedBranchId={copiedBranchId}
                onCopyAddress={handleCopyAddress}
              />
            ) : (
              <Tabs defaultValue={branches[0].id} className="w-full">
                {/* Tab Triggers */}
                <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-transparent p-0 mb-6">
                  {branches.map((branch) => (
                    <TabsTrigger
                      key={branch.id}
                      value={branch.id}
                      className={BRANCH_TAB_CLASS}
                    >
                      {branch.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* Tab Contents */}
                {branches.map((branch) => (
                  <TabsContent key={branch.id} value={branch.id} className="mt-0">
                    <BranchDetailPanel
                      branch={branch}
                      t={t}
                      copiedBranchId={copiedBranchId}
                      onCopyAddress={handleCopyAddress}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        )}

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div className="py-12 border-b border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-medium text-gray-900">
                {t("partner.reviews.title")}{" "}
                <span className="text-orange-500">
                  {partner.total_reviews || reviews.length}
                </span>
              </h2>
              {(partner.total_reviews || 0) > reviews.length && (
                <Link
                  href={`/${locale}/partner/${partner.slug}/reviews`}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 shrink-0"
                >
                  {t("reviews.viewAll")}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              )}
            </div>

            <div className="relative group">
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollReviewSlider("left")}
                className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Previous reviews"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div
                ref={reviewSliderRef}
                className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-4 px-4 scroll-px-4 sm:mx-0 sm:px-0 sm:scroll-px-0"
              >
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="shrink-0 w-[85%] sm:w-[320px] snap-start"
                  >
                    <ReviewCard
                      id={review.id}
                      rating={review.rating}
                      comment={review.comment}
                      translatedComment={review.translated_comment}
                      sourceLang={review.source_lang}
                      createdAt={review.created_at}
                      reviewerName={review.reviewer_name}
                      productName={review.product_name}
                      href={
                        review.product_id
                          ? `/${locale}/products/${review.product_id}`
                          : undefined
                      }
                    />
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollReviewSlider("right")}
                className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white shadow-md border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next reviews"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Safety Notice - Inline Banner */}
        <div className="py-8 border-t border-gray-100">
          <div className="flex items-center justify-center gap-3 text-sm text-gray-500">
            <Shield className="w-4 h-4 text-orange-400" />
            <span>{t("partner.safety.inline")}</span>
          </div>
        </div>
      </main>
    </div>
  );
}
